import { TX_MSG, calculateFeeWithMinimalDenom, convertDenomToMicroDenom, simulate } from '@/wallet-utils'
import { assert } from '@/utils'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { useMutation } from '@tanstack/react-query'
import { useStake } from '../../StakeProvider'
import { useLsmValidatorsQuery } from '../../queries'
import { MutationParameters } from './types'
import { useSelectedChain } from '@/contexts/wallet'
import { flushSync } from 'react-dom'
import { useSelectedDenom } from '../../../../contexts/denom'
import { CHAIN_INFO_LIST } from '@/config'

const useSignTokenizeSharesMutation = ({ setTokenizeSharesRaw }: MutationParameters) => {
  const { address, getSigningStargateClient } = useSelectedChain()

  const { data: lsmValidators } = useLsmValidatorsQuery()

  const { lsm } = useStake()

  const [denom] = useSelectedDenom()

  const handleMutation = async (): Promise<TxRaw> => {
    assert(address, `You are unable to tokenize shares without connecting your selected wallet for ${denom}.`)

    const selectedValidator = lsmValidators?.validators.find((validator) => {
      return validator.address === lsm.validatorAddress
    })

    const minimalDenom = CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom

    const amountInMicroDenom = convertDenomToMicroDenom(lsm.amount, minimalDenom)

    assert(selectedValidator, 'You are unable to send token without a selected validator.')

    const messages = [
      {
        typeUrl: TX_MSG.MsgTokenizeShares,
        value: {
          delegatorAddress: address,
          validatorAddress: selectedValidator.address,
          amount: {
            denom: minimalDenom,
            amount: amountInMicroDenom.toString()
          },
          tokenizedShareOwner: address
        }
      }
    ]

    const client = await getSigningStargateClient()

    const gas = await simulate(client, address, messages, '')

    const fee = calculateFeeWithMinimalDenom(minimalDenom, gas)

    return await client.sign(address, messages, fee, '')
  }

  const handleSuccess = (raw: TxRaw) => {
    // This is the only way to allow React v18 to update the state first before
    // the next mutation (which requires this state to have a value) is executed.
    flushSync(() => {
      setTokenizeSharesRaw(raw)
    })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignTokenizeSharesMutation }
